import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, Package, Users, FileText, LogOut } from 'lucide-react';

export const AdminSidebar = ({ currentPage }) => {
  const navigate = useNavigate();
  const { adminUser, adminLogout } = useAdminAuth();

  const navItems = [
    { id: 'products', label: 'Products', icon: Package, path: '/admin/portal/products' },
    { id: 'users', label: 'Users', icon: Users, path: '/admin/portal/users', adminOnly: true },
    { id: 'audit', label: 'Audit Log', icon: FileText, path: '/admin/portal/audit' },
  ];

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && adminUser?.role !== 'ADMIN') return false;
    return true;
  });

  return (
    <div className="w-64 bg-slate-900 text-white min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-emerald-400" />
          <span className="font-bold text-lg">PepFactor Admin</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {filteredNavItems.map(item => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
              currentPage === item.id
                ? 'bg-emerald-600 text-white'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </button>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-700">
        <div className="mb-4 px-4 py-2 bg-slate-800 rounded-lg">
          <p className="text-sm text-slate-400">Logged in as</p>
          <p className="font-medium truncate">{adminUser?.email}</p>
          <Badge className="mt-1 bg-emerald-600/20 text-emerald-400 text-xs">
            {adminUser?.role}
          </Badge>
        </div>
        <button
          onClick={() => {
            adminLogout();
            navigate('/admin/portal/login');
          }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
        >
          <LogOut className="h-5 w-5" />
          Log Out
        </button>
      </div>
    </div>
  );
};

export const AdminLayout = ({ children, currentPage }) => {
  const { adminUser, loading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !adminUser) {
      navigate('/admin/portal/login');
    }
  }, [loading, adminUser, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!adminUser) return null;

  return (
    <div className="flex min-h-screen bg-slate-100">
      <AdminSidebar currentPage={currentPage} />
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
